/**
 * Thread @-mention resolver — pure (group-threading P2b).
 *
 * Turns a sent message's text into the set of workspace-member uids to summon.
 * Resolves two forms against the account roster:
 *   1. `<@uid>` tokens (precise — inserted by the autocomplete).
 *   2. plain `@query` mentions (typed) — matched UNAMBIGUOUSLY against the roster by
 *      uid / email / email-local-part / whitespace-stripped displayName prefix.
 *
 * FAIL-CLOSED: a `@query` that matches zero OR more than one member is skipped (never
 * summon the wrong person). The server (`threadMembershipWrite` via P2a) stays the
 * sole authority — this only proposes uids. Pure + React-free.
 */
import { parseMentionUids } from './mentionParser';

export interface MentionRosterEntry {
  userId: string;
  displayName?: string;
  email?: string;
}

/** `@query` where `@` opens a word; query is name/email-ish chars (no spaces, no `@`). */
const PLAIN_MENTION = /(?:^|\s)@([A-Za-z0-9._%+-]+)/g;

function norm(v: string | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

/** Whitespace-stripped, lowercased name — so "QA Teammate" matches a "@qa" query. */
function nameKey(v: string | undefined): string {
  return norm(v).replace(/\s+/g, '');
}

function emailLocal(email: string | undefined): string {
  const e = norm(email);
  const at = e.indexOf('@');
  return at > 0 ? e.slice(0, at) : e;
}

/** Extract plain `@query` tokens (word-opening `@`), excluding `<@uid>` tokens. */
export function extractPlainMentionQueries(text: string): string[] {
  const out: string[] = [];
  if (!text) return out;
  PLAIN_MENTION.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PLAIN_MENTION.exec(text)) !== null) {
    const q = (m[1] ?? '').trim();
    if (q) out.push(q);
  }
  return out;
}

/**
 * Resolve a message's mentions to a de-duplicated list of roster uids to summon.
 * `<@uid>` tokens are kept only if they name a real roster member; plain `@query`
 * mentions resolve only when EXACTLY one member matches.
 */
export function resolveMentionsToUids(
  text: string,
  roster: ReadonlyArray<MentionRosterEntry>,
): string[] {
  const members = (roster ?? []).filter((m) => m && String(m.userId ?? '').trim());
  const byId = new Map<string, string>(); // uid → uid (identity, for membership check)
  for (const m of members) byId.set(String(m.userId).trim(), String(m.userId).trim());

  const result: string[] = [];
  const seen = new Set<string>();
  const add = (uid: string) => {
    const id = (uid ?? '').trim();
    if (id && byId.has(id) && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  };

  // 1. Precise <@uid> tokens (validated against the roster).
  for (const uid of parseMentionUids(text)) add(uid);

  // 2. Plain @query mentions — unambiguous match only.
  for (const rawQuery of extractPlainMentionQueries(text)) {
    const q = norm(rawQuery);
    if (!q) continue;
    const matches = members.filter((m) => {
      const id = norm(m.userId);
      const email = norm(m.email);
      return (
        id === q ||
        email === q ||
        emailLocal(m.email) === q ||
        (nameKey(m.displayName).length > 0 && nameKey(m.displayName).startsWith(q))
      );
    });
    if (matches.length === 1) add(String(matches[0].userId).trim());
    // 0 or >1 matches ⇒ ambiguous ⇒ skip (fail-closed).
  }

  return result;
}
